import dayjs, { Dayjs } from "dayjs";
import {
  IPayPeriodTemplate,
  PayPeriodTemplateStartDayOfWeek,
} from "@/business/business";
import {
  PAY_PERIOD_TEMPLATE_CADENCE,
  PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK,
} from "@/business/constants";

export interface PeriodBoundaries {
  start: Dayjs;
  end: Dayjs;
}

const DAY_OF_WEEK_MAP: Record<PayPeriodTemplateStartDayOfWeek, number> = {
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.sunday]: 0,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.monday]: 1,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.tuesday]: 2,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.wednesday]: 3,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.thursday]: 4,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.friday]: 5,
  [PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK.saturday]: 6,
};

const calculateWeeklyPeriod = (
  date: Dayjs,
  startDayOfWeek: PayPeriodTemplateStartDayOfWeek,
): PeriodBoundaries => {
  const targetDay = DAY_OF_WEEK_MAP[startDayOfWeek];
  const currentDay = date.day();

  let daysToSubtract = currentDay - targetDay;
  if (daysToSubtract < 0) {
    daysToSubtract += 7;
  }

  const start = date.subtract(daysToSubtract, "day").startOf("day");
  const end = start.add(6, "day").endOf("day");

  return { start, end };
};

const calculateFirstAndFifteenthPeriod = (date: Dayjs): PeriodBoundaries => {
  if (date.date() <= 15) {
    const start = date.startOf("month");
    const end = date.date(15).endOf("day");
    return { start, end };
  } else {
    const start = date.date(16).startOf("day");
    const end = date.endOf("month");
    return { start, end };
  }
};

const calculateMonthlyPeriod = (date: Dayjs): PeriodBoundaries => ({
  start: date.startOf("month"),
  end: date.endOf("month"),
});

const calculateEveryOtherWeekPeriod = (
  date: Dayjs,
  referenceDate: string,
): PeriodBoundaries => {
  const reference = dayjs(referenceDate).startOf("day");

  const daysSinceReference = date.diff(reference, "day");

  const cycleNumber = Math.floor(daysSinceReference / 14);

  const start = reference.add(cycleNumber * 14, "day");

  const end = start.add(13, "day").endOf("day");

  return { start, end };
};

export const calculatePeriodBoundaries = (
  date: Dayjs,
  template: IPayPeriodTemplate,
): PeriodBoundaries => {
  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.monthly) {
    return calculateMonthlyPeriod(date);
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.first_and_fifteenth) {
    return calculateFirstAndFifteenthPeriod(date);
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.every_other_week) {
    return calculateEveryOtherWeekPeriod(
      date,
      template.period_start_reference_date,
    );
  }

  return calculateWeeklyPeriod(date, template.start_day_of_week);
};

export const getNextPeriodStart = (
  currentStart: Dayjs,
  template: IPayPeriodTemplate,
): Dayjs => {
  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.monthly) {
    return currentStart.add(1, "month").startOf("month");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.weekly) {
    return currentStart.add(7, "day");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.first_and_fifteenth) {
    if (currentStart.date() === 1) {
      return currentStart.date(16);
    }
    return currentStart.add(1, "month").date(1).startOf("day");
  }

  return currentStart.add(14, "day");
};

const getPreviousPeriodStart = (
  currentStart: Dayjs,
  template: IPayPeriodTemplate,
): Dayjs => {
  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.monthly) {
    return currentStart.subtract(1, "month").startOf("month");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.weekly) {
    return currentStart.subtract(7, "day");
  }

  if (template.cadence === PAY_PERIOD_TEMPLATE_CADENCE.first_and_fifteenth) {
    if (currentStart.date() === 1) {
      return currentStart.subtract(1, "month").date(16).startOf("day");
    }
    return currentStart.date(1).startOf("day");
  }

  return currentStart.subtract(14, "day");
};

export const generatePayrollPeriods = (
  template: IPayPeriodTemplate,
  pastPeriods: number = 12,
  futurePeriods: number = 4,
): PeriodBoundaries[] => {
  const periods: PeriodBoundaries[] = [];
  const now = dayjs();

  const { start: currentStart } = calculatePeriodBoundaries(now, template);

  let periodStart = currentStart;
  for (let i = 0; i < pastPeriods; i++) {
    periodStart = getPreviousPeriodStart(periodStart, template);
    periods.unshift(calculatePeriodBoundaries(periodStart, template));
  }

  periods.push(calculatePeriodBoundaries(now, template));

  periodStart = currentStart;
  for (let i = 0; i < futurePeriods; i++) {
    periodStart = getNextPeriodStart(periodStart, template);
    periods.push(calculatePeriodBoundaries(periodStart, template));
  }

  return periods;
};
